import React, { useState, useEffect } from "react";
import QRCode from "react-qr-code";
import { useAuth } from "../context/AuthContext";
import "../theme.css";
import "./Settings.css";

export default function Settings() {
  const { userInfo, loading, updateUser } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [language, setLanguage] = useState("pt");
  const [darkMode, setDarkMode] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [mfaSecret, setMfaSecret] = useState("");

  // Inicializa as preferências a partir de userInfo
  useEffect(() => {
    if (!loading && userInfo) {
      setNewEmail("");
      setLanguage(userInfo.language);
      setDarkMode(userInfo.darkMode);
      setMfaEnabled(userInfo.mfaEnabled);
    }
  }, [loading, userInfo]);

  // Aplica/remova a classe `dark` no `<html>` conforme darkMode
  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [darkMode]);

  // Gera o QR code se habilitar MFA
  useEffect(() => {
    if (mfaEnabled && userInfo.username) {
      const rand = () => Math.random().toString(36).substring(2, 10).toUpperCase();
      setMfaSecret(
        `otpauth://totp/Viadrive:${userInfo.username}?secret=${rand()}&issuer=Viadrive`
      );
    }
  }, [mfaEnabled, userInfo.username]);

  // Função genérica para salvar um campo via updateUser
  const save = async (field, value) => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await updateUser({ [field]: value });
      setMessage("Preferência atualizada com sucesso!");
    } catch (e) {
      console.error(e);
      setError("Falha ao atualizar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p>Carregando configurações…</p>;
  }

  return (
    <div className="container-page dashboard-content" style={{ marginLeft: 0 }}>
      <h2>Configurações</h2>

      {error && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {/* — Detalhes de conta (readonly) — */}
      <div className="card p-4 mb-4">
        <h5>Detalhes da Conta</h5>
        <div className="row mb-2">
          <div className="col-sm-3">Usuário:</div>
          <div className="col-sm-9">{userInfo.username}</div>
        </div>
        <div className="row mb-2">
          <div className="col-sm-3">Criado em:</div>
          <div className="col-sm-9">
            {userInfo.createdAt
              ? new Date(userInfo.createdAt.replace(" ", "T") + "Z").toLocaleString("pt-BR")
              : "–"}
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-sm-3">Status:</div>
          <div className="col-sm-9">{userInfo.status === "ativo" ? "Ativo" : "Inativo"}</div>
        </div>
        <div className="row mb-2">
          <div className="col-sm-3">Permissão:</div>
          <div className="col-sm-9">{userInfo.role}</div>
        </div>
      </div>

      {/* — Preferências editáveis — */}
      <div className="card p-4">
        {/* E-mail */}
        <div className="mb-3">
          <label className="form-label">E-mail atual</label>
          <input
            type="email"
            className="form-control mb-1"
            value={userInfo.email}
            disabled
          />
          <div className="d-flex">
            <input
              type="email"
              className="form-control me-2"
              placeholder="Novo e-mail"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={saving}
            />
            <button
              className="btn btn-outline-primary"
              onClick={() => save("email", newEmail)}
              disabled={saving || !newEmail}
            >
              {saving ? "Salvando…" : "Alterar"}
            </button>
          </div>
        </div>

        {/* Idioma */}
        <div className="mb-3">
          <label className="form-label">Idioma</label>
          <select
            className="form-select"
            value={language}
            onChange={(e) => { const v = e.target.value; setLanguage(v); save("language", v); }}
            disabled={saving}
          >
            <option value="pt">Português</option>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>

        {/* Dark Mode */}
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="switchDarkMode"
            checked={darkMode}
            onChange={() => { const nxt = !darkMode; setDarkMode(nxt); save("darkMode", nxt); }}
            disabled={saving}
          />
          <label className="form-check-label" htmlFor="switchDarkMode">
            Modo Escuro
          </label>
        </div>

        {/* MFA */}
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="switchMfa"
            checked={mfaEnabled}
            onChange={() => { const nxt = !mfaEnabled; setMfaEnabled(nxt); save("mfaEnabled", nxt); }}
            disabled={saving}
          />
          <label className="form-check-label" htmlFor="switchMfa">
            Habilitar MFA
          </label>
        </div>

        {mfaEnabled && (
          <div className="mb-3 text-center">
            <QRCode value={mfaSecret} size={128} />
            <p className="mt-2 text-muted small">
              Escaneie este QR Code no seu app de autenticação.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
